import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getLoginCredentialDefinition,
  getCsvHeaders,
  LoginCredentialDefinition,
  MediaType,
} from '../../api/media';
import type { AsyncStatus } from '../../interfaces';

export interface LoginCredentialField {
  name: string;
  label: string;
}

interface UseMediaFormSliceState {
  selectedMediaId: number | null;
  selectedMediaType: MediaType | null;

  loginCredentialTemplateStatus: AsyncStatus;
  loginCredentialFields: LoginCredentialField[];

  csvHeadersStatus: AsyncStatus;
  csvHeaders: string[];
}

const initialState: UseMediaFormSliceState = {
  selectedMediaId: null,
  selectedMediaType: null,
  loginCredentialTemplateStatus: 'idle',
  loginCredentialFields: [],
  csvHeadersStatus: 'idle',
  csvHeaders: [],
};

export const fetchLoginCredentialTemplate = createAsyncThunk(
  'useMediaForm/fetchLoginCredentialTemplate',
  async (mediaId: number) => {
    const result: LoginCredentialDefinition = await getLoginCredentialDefinition(mediaId);
    let fields: LoginCredentialField[] = [];
    if (result.template) {
      try {
        const parsed: unknown = JSON.parse(result.template);
        fields = Array.isArray(parsed) ? (parsed as LoginCredentialField[]) : [];
      } catch {
        fields = [];
      }
    }
    return { type: result.type, fields };
  },
);

export const fetchCsvHeaders = createAsyncThunk('useMediaForm/fetchCsvHeaders', (mediaId: number) =>
  getCsvHeaders(mediaId),
);

const useMediaFormSlice = createSlice({
  name: 'useMediaForm',
  initialState,
  reducers: {
    setSelectedMediaId(state, action: { payload: number | null }) {
      state.selectedMediaId = action.payload;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoginCredentialTemplate.pending, (state) => {
        // Clear type and fields immediately so the form doesn't show stale credential
        // fields for the previously selected media while the new template loads.
        state.loginCredentialTemplateStatus = 'loading';
        state.selectedMediaType = null;
        state.loginCredentialFields = [];
      })
      .addCase(fetchLoginCredentialTemplate.fulfilled, (state, action) => {
        state.loginCredentialTemplateStatus = 'succeeded';
        state.selectedMediaType = action.payload.type;
        state.loginCredentialFields = action.payload.fields;
      })
      .addCase(fetchLoginCredentialTemplate.rejected, (state) => {
        state.loginCredentialTemplateStatus = 'failed';
        state.selectedMediaType = null;
        state.loginCredentialFields = [];
      })
      .addCase(fetchCsvHeaders.pending, (state) => {
        state.csvHeadersStatus = 'loading';
        state.csvHeaders = [];
      })
      .addCase(fetchCsvHeaders.fulfilled, (state, action) => {
        state.csvHeadersStatus = 'succeeded';
        state.csvHeaders = action.payload;
      })
      .addCase(fetchCsvHeaders.rejected, (state) => {
        state.csvHeadersStatus = 'failed';
        state.csvHeaders = [];
      });
  },
});

export const { setSelectedMediaId, reset: resetUseMediaForm } = useMediaFormSlice.actions;
export default useMediaFormSlice.reducer;
